import { PlaylistSong, EnrichedSong, Playlist } from "../../../types/PlaylistTypes";
import { authFetch } from "../../../utils/authUtils";

// Fetch songs for search functionality
export const fetchSongsForSearch = async (): Promise<PlaylistSong[]> => {
    const response = await fetch('/api/songs/search');
    if (!response.ok) {
        throw new Error(`Failed to fetch songs: ${response.status}`);
    }
    return response.json();
};

// Fetch detailed information for a single song
export const fetchSongDetails = async (songId: number): Promise<any> => {
    const response = await authFetch(`/api/songs/details/${songId}`);
    if (!response.ok) {
        throw new Error(`Failed to fetch song details: ${response.status}`);
    }
    return response.json();
};

// Enrich a list of songs with detailed information
export const enrichSongs = async (songs: PlaylistSong[]): Promise<EnrichedSong[]> => {
    if (!songs.length) return [];

    const enrichedPromises = songs.map(async (song) => {
        if (!song || !song.melon_song_id) {
            console.error('Invalid song object:', song);
            return null;
        }

        try {
            const songData = await fetchSongDetails(song.melon_song_id);
            return {
                ...song,
                ...songData,
            };
        } catch (err) {
            console.error(`Error fetching song details for ${song.melon_song_id}:`, err);
            return song;
        }
    });

    const enrichedData = await Promise.all(enrichedPromises);
    return enrichedData.filter((song): song is EnrichedSong => song !== null);
};

// Fetch playlist details
export const fetchPlaylistDetails = async (playlistId: string): Promise<{
    playlist: Playlist;
    songs: PlaylistSong[];
}> => {
    // Fetch playlist info
    const playlistResponse = await authFetch(`/api/playlists/${playlistId}`);
    if (!playlistResponse.ok) {
        throw new Error(`Failed to fetch playlist: ${playlistResponse.status}`);
    }
    const playlist = await playlistResponse.json();

    // Fetch playlist songs
    const songsResponse = await authFetch(`/api/playlists/${playlistId}/songs`);
    if (!songsResponse.ok) {
        throw new Error(`Failed to fetch songs: ${songsResponse.status}`);
    }
    const songs = await songsResponse.json();

    return { playlist, songs };
};

// Update playlist metadata
export const updatePlaylistMetadata = async (
    playlistId: string,
    data: {
        name: string;
        description: string;
        is_public: boolean;
        theme: string;
    }
): Promise<Playlist> => {
    const response = await authFetch(`/api/playlists/${playlistId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update playlist: ${response.status} - ${errorText}`);
    }

    return response.json();
};

// Update playlist songs (replace all songs)
export const updatePlaylistSongs = async (
    playlistId: string,
    songs: PlaylistSong[]
): Promise<void> => {
    // Get current songs
    const currentSongsResponse = await authFetch(`/api/playlists/${playlistId}/songs`);
    if (!currentSongsResponse.ok) {
        throw new Error(`Failed to fetch current songs: ${currentSongsResponse.status}`);
    }
    const currentSongs = await currentSongsResponse.json();

    // Remove all existing songs
    for (const song of currentSongs) {
        await authFetch(`/api/playlists/${playlistId}/songs/${song.melon_song_id}`, {
            method: 'DELETE',
        });
    }

    // Add all new songs in order
    for (let i = 0; i < songs.length; i++) {
        const song = songs[i];
        await authFetch(`/api/playlists/${playlistId}/songs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                song_id: song.melon_song_id,
                position: i + 1
            }),
        });
    }
};

// Create a new playlist
export const createPlaylist = async (data: {
    name: string;
    description: string;
    is_public: boolean;
    song_ids: number[];
    userId: number;
    theme: string;
}): Promise<Playlist> => {
    const response = await fetch("/api/playlists", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error("Failed to create playlist");
    }

    return response.json();
};

// Delete a playlist
export const deletePlaylist = async (playlistId: string): Promise<void> => {
    const response = await authFetch(`/api/playlists/${playlistId}`, {
        method: 'DELETE'
    });

    if (!response.ok) {
        throw new Error(`Failed to delete playlist: ${response.status}`);
    }
}; 